import React, { useEffect, useState, PropsWithChildren } from "react";
import { IconWithLabel, Button, RectangleButton } from "../../../Actions";
import { useWizard } from "react-use-wizard";
import { CopyButton } from "../../../CopyButton";
import { StepContent } from "../Components";
import SmileyWink from "../../../../assets/SmileyWink.svg";
import SmileyMeh from "../../../../assets/SmileyMeh.svg";
import { useUserContext } from "../../../../contexts/User";
import { getReferrals } from "../../../../utils/api/users";
import PageLoader from "../../../LoadingIndicators/PageLoader";
import { StatusBanner } from "../../../StatusBanner/StatusBanner";

const inviteText: Record<string, any> = {
  0: {
    title: (
      <>
        You and your partner distrusted each other.{" "}
        <span className="text-coinage-purple">
          Looks like you found your match.
        </span>
      </>
    ),
    description: (
      <>
        You’ve earned <b className="text-coinage-orange">0 invitations.</b> You
        have to lie in the bed you make.
      </>
    )
  },
  2: {
    title: (
      <>
        You trusted your partner, but your partner distrusted you.{" "}
        <span className="text-coinage-orange">
          This is how villains are born.
        </span>
      </>
    ),
    description: (
      <>
        For your trusting nature in a trustless world, you’ve earned{" "}
        <b className="text-coinage-orange">2 invitations.</b> Please distribute
        these unique invitations to people you trust and know would value
        joining the Coinage Network. <br />{" "}
        <span className="bold">You have 5 days to send your invitations.</span>
        Each link may only be used once.
        <br />
        <br /> Just one thing we ask that you keep in mind: the more diverse the
        Network, the stronger the Network. We&apos;re here to escape the echo
        chamber, not reinforce it.
      </>
    )
  },
  4: {
    title: (
      <>
        You and your partner trusted each other.{" "}
        <span className="text-coinage-orange"> Well done.</span>
      </>
    ),
    description: (
      <>
        You’ve earned <b className="text-coinage-orange">4 invitations.</b>{" "}
        Please distribute these unique invitations to people you trust and know
        would value joining the Coinage Network. <br />{" "}
        <span className="bold">You have 5 days to send your invitations.</span>
        Each link may only be used once. <br />
        <br />
        Just one thing we ask that you keep in mind: the more diverse the
        Network, the stronger the Network. We&apos;re here to escape the echo
        chamber, not reinforce it.
      </>
    )
  },
  6: {
    title: (
      <>
        Your partner trusted you, but you distrusted them.{" "}
        <span className="text-coinage-orange">How could you?</span>
      </>
    ),
    description: (
      <>
        For risking it all, you’ve earned{" "}
        <b className="text-coinage-orange">6 invitations.</b> Please distribute
        these unique invitations to people you trust and know would value
        joining the Coinage Network. <br />{" "}
        <span className="bold">You have 5 days to send your invitations.</span>
        Each link may only be used once. <br />
        <br />
        Just one thing we ask that you keep in mind: the more diverse the
        Network, the stronger the Network. We&apos;re here to escape the echo
        chamber, not reinforce it.
      </>
    )
  }
} as const;

const fallbackInviteText = {
  title: <>These are your invites</>,
  description: (
    <>
      Please distribute these unique invitations to people you trust and know
      would value joining the Coinage Network. <br />{" "}
      <span className="bold">You have 5 days to send your invitations.</span>
      Each link may only be used once. Each link may only be used once.
    </>
  )
};

export const InviteComponent: React.FC<
  PropsWithChildren<{ onSuccess: () => any }>
> = ({ onSuccess }) => {
  const { user, updateUserState } = useUserContext();
  const [appState, setAppState] = useState<null | "loading" | "error">(
    "loading"
  );
  const [referralCodes, setReferralCodes] = useState<string[] | null>(null);

  useEffect(() => {
    async function setReferrals() {
      if (!user?.id) return;
      const { data, error } = await getReferrals(user?.id);
      if (error) {
        setAppState("error");
      }
      setReferralCodes(data.referralCodes);
      updateUserState({ numInvites: data.referralCodes.length });
      setAppState(null);
    }
    setReferrals();
  }, [user?.id]);

  const generateReferralUrl = (code: string) => {
    const baseBath = window.location.origin;
    return `${baseBath}/network/${code}`;
  };

  if (appState === "loading" || !referralCodes) return <PageLoader />;
  if (appState === "error")
    return (
      <StatusBanner title="Sorry something went wrong">
        Please refresh
      </StatusBanner>
    );

  const numInvites = referralCodes?.length;

  const content = inviteText[numInvites] ?? fallbackInviteText;

  const allUserLinks = referralCodes
    .map(referralCode => generateReferralUrl(referralCode))
    .join("\n");

  return (
    <StepContent title={content.title} description={content.description}>
      {numInvites !== 0 ? (
        <div className="w-full flex flex-col items-center gap-4 my-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-4xl my-4">
            {referralCodes.map((referralCode, idx) => (
              <CopyButton
                buttonLabel={`Invitation #${idx + 1}`}
                textToCopy={generateReferralUrl(referralCode)}
                key={referralCode}>
                <IconWithLabel icon="copy" label="Copy Link" />
              </CopyButton>
            ))}
          </div>

          <CopyButton isCopyAll textToCopy={allUserLinks}>
            Copy All Links
          </CopyButton>

          <Button
            analyticsProperty="Got them"
            modifier="primary"
            onClick={onSuccess}>
            Got Them
          </Button>
        </div>
      ) : (
        <div className="flex gap-6 w-full">
          <RectangleButton
            analyticsProperty="i regret nothing"
            color="teal"
            onClick={onSuccess}>
            <SmileyWink width="40" className="text-coinage-teal" />I regret
            nothing
          </RectangleButton>
          <RectangleButton
            analyticsProperty="i regret everything"
            color="orange"
            onClick={onSuccess}>
            <SmileyMeh width="40" className="text-coinage-orange" />I regret
            everything
          </RectangleButton>
        </div>
      )}
    </StepContent>
  );
};
const Invitations: React.FC = () => {
  const { nextStep } = useWizard();
  return <InviteComponent onSuccess={nextStep} />;
};

export default Invitations;
